import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import gsap from "gsap";
import {
  fetchTrainingImages,
  fetchTrainingStats,
  fetchTrainingFaces,
  markTrainingDone,
  assignPerson,
  createPerson,
  addTeamMember,
  ignoreFace,
  unignoreFace,
  fetchPropagateCandidates,
  bulkAssignPerson,
  uploadTrainingImages,
  deleteUntrainedImages,
} from "../api";
import Modal from "../shared/components/ui/Modal/Modal";
import Button from "../shared/components/ui/Button/Button";
import Toast, { useToast } from "../shared/components/ui/Toast/Toast";
import { PersonModal } from "../features/edit/components";
import usePersonForm from "../hooks/usePersonForm";

// =============================================================================
// Training Page - Face Recognition Bootstrap
// =============================================================================

// -----------------------------------------------------------------------------
// Helper functions for face state and styling (extracted to reduce complexity)
// -----------------------------------------------------------------------------

/**
 * Get the stroke color for a face bounding box based on its state
 */
function getFaceBboxStroke(isSelected, isLabeled, isIgnored) {
  if (isSelected || isLabeled) return "#00ff88";
  if (isIgnored) return "#666";
  return "#ff6b6b";
}

/**
 * Get the label text for a face bounding box
 */
function getFaceBboxLabel(face, idx, isLabeled, isIgnored) {
  if (isLabeled) return face.person_name || "OK";
  if (isIgnored) return "x";
  return `#${idx + 1}`;
}

/**
 * Get the border style for a face thumbnail based on its state
 */
function getFaceThumbBorder(face, isSelected) {
  if (face.person) return "2px solid var(--accent-strong)";
  if (face.ignored) return "2px solid var(--muted)";
  if (isSelected) return "2px solid var(--accent)";
  return "2px solid transparent";
}

/**
 * Get the placeholder icon for a face thumbnail
 */
function getFaceThumbIcon(face) {
  if (face.person) return "OK";
  if (face.ignored) return "x";
  return "?";
}

/**
 * Get the color for a face thumbnail icon
 */
function getFaceThumbIconColor(face) {
  if (face.person) return "var(--accent)";
  if (face.ignored) return "var(--muted)";
  return "var(--text)";
}

/**
 * Get the display name for a face thumbnail label
 */
function getFaceThumbDisplayName(face) {
  if (face.person_name) return face.person_name;
  if (face.person_display) return face.person_display;
  if (face.ignored) return "Ignored";
  return "Unlabeled";
}

/**
 * Build CSS class names for a face thumbnail
 */
function getFaceThumbClassName(face, isSelected) {
  const classes = ["face-thumb"];
  if (isSelected) classes.push("selected");
  if (face.person) classes.push("labeled");
  if (face.ignored) classes.push("ignored");
  return classes.join(" ");
}

// -----------------------------------------------------------------------------
// FaceBboxOverlay - SVG overlay for face bounding boxes
// -----------------------------------------------------------------------------

function FaceBboxOverlay({ faces, selectedFace, onFaceClick }) {
  return faces.map((face, idx) => {
    // All coordinates use XYXY format per CLAUDE.md
    const bbox = face.bbox || face.frame_bbox;
    if (!bbox || bbox.length !== 4) return null;

    const [x1, y1, x2, y2] = bbox;
    const isSelected = selectedFace?.id === face.id;
    const isLabeled = !!face.person;
    const isIgnored = face.ignored;
    const strokeColor = getFaceBboxStroke(isSelected, isLabeled, isIgnored);
    const labelText = getFaceBboxLabel(face, idx, isLabeled, isIgnored);

    return (
      <g
        key={face.id}
        style={{ pointerEvents: "all", cursor: "pointer" }}
        onClick={() => onFaceClick(face)}
      >
        <rect
          x={x1}
          y={y1}
          width={x2 - x1}
          height={y2 - y1}
          fill="none"
          stroke={strokeColor}
          strokeWidth={isSelected ? 4 : 2}
          opacity={isIgnored ? 0.4 : 0.9}
        />
        <text
          x={x1 + 4}
          y={y1 + 18}
          fill={strokeColor}
          fontSize="16"
          fontWeight="bold"
          style={{ textShadow: "0 0 4px #000, 0 0 8px #000" }}
        >
          {labelText}
        </text>
      </g>
    );
  });
}

// -----------------------------------------------------------------------------
// FaceThumb - Individual face thumbnail in the grid
// -----------------------------------------------------------------------------

function FaceThumb({ face, isSelected, onFaceClick, onIgnore, onUnignore, previewUrl, imageWidth, imageHeight }) {
  const handleContextMenu = useCallback((e) => {
    e.preventDefault();
    if (face.ignored) {
      onUnignore(face);
    } else if (!face.person) {
      onIgnore(face);
    }
  }, [face, onIgnore, onUnignore]);

  const handleUnignoreClick = useCallback((e) => {
    e.stopPropagation();
    onUnignore(face);
  }, [face, onUnignore]);

  const className = getFaceThumbClassName(face, isSelected);
  const borderStyle = getFaceThumbBorder(face, isSelected);
  const iconText = getFaceThumbIcon(face);
  const iconColor = getFaceThumbIconColor(face);
  const displayName = getFaceThumbDisplayName(face);

  // Calculate face crop from bbox (XYXY format)
  const bbox = face.bbox || [];
  const hasBbox = bbox.length === 4 && previewUrl && imageWidth > 0 && imageHeight > 0;

  let cropStyle = null;
  if (hasBbox) {
    const [x1, y1, x2, y2] = bbox;
    const faceW = x2 - x1;
    const faceH = y2 - y1;
    const faceCenterX = x1 + faceW / 2;
    const faceCenterY = y1 + faceH / 2;

    // Calculate scale to fit face in 60x60 container with some padding
    const thumbSize = 60;
    const padding = 1.3; // Show 30% more than just the face
    const scale = thumbSize / (Math.max(faceW, faceH) * padding);

    // Calculate the scaled image size
    const scaledW = imageWidth * scale;
    const scaledH = imageHeight * scale;

    // Position to center the face in the container
    const offsetX = (thumbSize / 2) - (faceCenterX * scale);
    const offsetY = (thumbSize / 2) - (faceCenterY * scale);

    cropStyle = {
      width: scaledW,
      height: scaledH,
      position: "absolute",
      left: offsetX,
      top: offsetY,
    };
  }

  return (
    <div
      className={className}
      onClick={() => onFaceClick(face)}
      onContextMenu={handleContextMenu}
      style={{
        width: 72,
        height: 90,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        cursor: "pointer",
        padding: 4,
        borderRadius: 8,
        border: borderStyle,
        background: face.ignored ? "rgba(128,128,128,0.1)" : "rgba(10,20,30,0.5)",
        opacity: face.ignored ? 0.5 : 1,
        transition: "all 0.15s ease",
      }}
    >
      <div style={{
        width: 60,
        height: 60,
        borderRadius: 6,
        overflow: "hidden",
        background: "#111",
        position: "relative",
      }}>
        {hasBbox ? (
          <img
            src={previewUrl}
            alt=""
            draggable={false}
            style={cropStyle}
          />
        ) : (
          <div style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 20,
            color: iconColor,
          }}>
            {iconText}
          </div>
        )}
        {/* Unignore button for ignored faces */}
        {face.ignored && (
          <button
            onClick={handleUnignoreClick}
            style={{
              position: "absolute",
              top: 2,
              right: 2,
              width: 20,
              height: 20,
              padding: 0,
              border: "none",
              borderRadius: 4,
              background: "rgba(100,200,150,0.9)",
              color: "#000",
              fontSize: 12,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            title="Restore this face"
          >
            &lt;-
          </button>
        )}
      </div>
      <span style={{
        fontSize: 10,
        marginTop: 4,
        maxWidth: 68,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        color: face.person ? "var(--accent)" : "var(--muted)",
      }}>
        {displayName}
      </span>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Main Training Component
// -----------------------------------------------------------------------------

export default function Training() {
  // State
  const [images, setImages] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [faces, setFaces] = useState([]);
  const [selectedFace, setSelectedFace] = useState(null);
  const [loading, setLoading] = useState(true);
  // Error state for upload failures (displayed via Toast)
const [, setError] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Person assignment modal (shared hook from Edit page)
  const {
    showPersonModal,
    personModalPos,
    personForm,
    setPersonForm,
    modalPersonId,
    setModalPersonId,
    personSuggestions,
    teamSearchQuery,
    setTeamSearchQuery,
    teamSuggestions,
    selectedTeam,
    openPersonModal,
    closePersonModal,
    handleModalDragStart,
    selectTeam,
    clearTeam,
  } = usePersonForm();
  const [savingPerson, setSavingPerson] = useState(false);

  // Image display state for overlay
  const [imgDims, setImgDims] = useState({ naturalW: 0, naturalH: 0, displayW: 0, displayH: 0 });
  const imgRef = useRef(null);

  // Animation refs
  const tabsRef = useRef(null);
  const statsCardRef = useRef(null);
  const imageListRef = useRef(null);
  const propagateCardsRef = useRef([]);
  const animatedRef = useRef({ tabs: false, stats: false, images: false });

  // View mode: "label" or "propagate"
  const [viewMode, setViewMode] = useState("label");

  // Propagate view state
  const [propagateCandidates, setPropagateCandidates] = useState([]);
  const [propagateSummary, setPropagateSummary] = useState(null);
  const [propagateLoading, setPropagateLoading] = useState(false);
  const [selectedMatches, setSelectedMatches] = useState({}); // { personId: Set<faceId> }
  const [propagateThreshold, setPropagateThreshold] = useState(0.5);
  const [assigningPerson, setAssigningPerson] = useState(null);

  // Upload state
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  // Delete all state
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Toast notifications
  const { toast, showToast, hideToast } = useToast();

  // Load training images and stats
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [imagesRes, statsRes] = await Promise.all([
        fetchTrainingImages("ready"),
        fetchTrainingStats(),
      ]);
      setImages(Array.isArray(imagesRes) ? imagesRes : imagesRes.results || []);
      setStats(statsRes);
      setError(null);
    } catch (e) {
      console.error("Failed to load training data:", e);
      setError("Failed to load training data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 10000);
    return () => clearInterval(interval);
  }, [loadData]);

  // Load faces when image is selected
  useEffect(() => {
    if (!selectedImage) {
      setFaces([]);
      setSelectedFace(null);
      return;
    }
    const img = images.find((i) => i.id === selectedImage);
    if (img?.faces) {
      setFaces(img.faces);
    } else {
      fetchTrainingFaces(selectedImage)
        .then((data) => setFaces(Array.isArray(data) ? data : []))
        .catch((e) => console.error("Failed to load faces:", e));
    }
  }, [selectedImage, images]);

  // Auto-select first image
  useEffect(() => {
    if (!selectedImage && images.length > 0) {
      setSelectedImage(images[0].id);
    }
  }, [images, selectedImage]);

  const currentImage = useMemo(
    () => images.find((i) => i.id === selectedImage),
    [images, selectedImage]
  );

  // Reset image dimensions when image changes
  useEffect(() => {
    setImgDims({ naturalW: 0, naturalH: 0, displayW: 0, displayH: 0 });
  }, [selectedImage]);

  // Animation: tabs on mount
  useEffect(() => {
    if (tabsRef.current && !animatedRef.current.tabs) {
      const buttons = tabsRef.current.querySelectorAll("button");
      if (buttons.length) {
        gsap.fromTo(buttons, { opacity: 0, y: -10 }, { opacity: 1, y: 0, duration: 0.3, stagger: 0.1, ease: "power2.out" });
        animatedRef.current.tabs = true;
      }
    }
  }, []);

  // Animation: stats card when data loads
  useEffect(() => {
    if (statsCardRef.current && stats && !animatedRef.current.stats) {
      gsap.fromTo(statsCardRef.current, { opacity: 0, x: 15 }, { opacity: 1, x: 0, duration: 0.4, ease: "power2.out" });
      animatedRef.current.stats = true;
    }
  }, [stats]);

  // Animation: image list items when images load
  useEffect(() => {
    if (imageListRef?.current && images.length > 0 && !animatedRef.current.images) {
      const items = imageListRef.current.querySelectorAll("[data-image-item]");
      if (items.length) {
        gsap.fromTo(items, { opacity: 0, x: 10 }, { opacity: 1, x: 0, duration: 0.3, stagger: 0.03, ease: "power2.out" });
        animatedRef.current.images = true;
      }
    }
  }, [images]);

  // Animation: propagate candidates when they load
  useEffect(() => {
    const cards = propagateCardsRef.current.filter(Boolean);
    if (cards.length > 0 && viewMode === "propagate") {
      gsap.fromTo(cards, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.4, stagger: 0.08, ease: "power2.out" });
    }
  }, [propagateCandidates, viewMode]);

  // Arrow key navigation for image list
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't handle if modal is open or user is typing
      if (showPersonModal || e.target.tagName === "INPUT") return;

      const currentIndex = images.findIndex((i) => i.id === selectedImage);
      if (currentIndex === -1) return;

      if (e.key === "ArrowDown" || e.key === "j") {
        e.preventDefault();
        const nextIndex = Math.min(currentIndex + 1, images.length - 1);
        setSelectedImage(images[nextIndex].id);
      } else if (e.key === "ArrowUp" || e.key === "k") {
        e.preventDefault();
        const prevIndex = Math.max(currentIndex - 1, 0);
        setSelectedImage(images[prevIndex].id);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [images, selectedImage, showPersonModal]);

  // Upload handlers
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  }, []);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = Array.from(e.dataTransfer.files).filter((f) =>
      f.type.startsWith("image/")
    );
    if (files.length === 0) return;

    setUploading(true);
    try {
      await uploadTrainingImages(files);
      // Reload data after successful upload
      await loadData();
    } catch (err) {
      console.error("Upload failed:", err);
      setError("Upload failed: " + (err.response?.data?.error || err.message));
    } finally {
      setUploading(false);
    }
  }, [loadData]);

  const handleFileSelect = useCallback(async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      await uploadTrainingImages(files);
      await loadData();
    } catch (err) {
      console.error("Upload failed:", err);
      setError("Upload failed: " + (err.response?.data?.error || err.message));
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, [loadData]);

  const handleFaceClick = (face) => {
    setSelectedFace(face);
    if (!face.person && !face.ignored) {
      openPersonModal({ face });
    }
  };

  const handleSavePerson = async () => {
    if (!selectedFace) return;
    const hasName = personForm.first_name?.trim() || personForm.last_name?.trim();
    if (!hasName && !modalPersonId) return;

    setSavingPerson(true);
    try {
      let personId;

      if (modalPersonId) {
        personId = modalPersonId;
      } else {
        // Create new person - build payload without team_id (handled via membership)
        const payload = {
          prefix: personForm.prefix,
          first_name: personForm.first_name,
          last_name: personForm.last_name,
          suffix: personForm.suffix,
          jersey_number: personForm.jersey_number === "" ? null : Number(personForm.jersey_number),
          display_name: `${personForm.prefix ? personForm.prefix + " " : ""}${personForm.first_name || ""} ${personForm.last_name || ""} ${personForm.suffix || ""}`.trim(),
        };
        const newPerson = await createPerson(payload);
        personId = newPerson.id;

        // Link to team (matches Edit.jsx pattern)
        if (personForm.team_id) {
          const jerseyNum = personForm.jersey_number === "" ? null : Number(personForm.jersey_number);
          await addTeamMember(personForm.team_id, personId, jerseyNum);
        }
      }

      // Assign to face
      await assignPerson(selectedFace.id, personId);
      setFaces((prev) =>
        prev.map((f) =>
          f.id === selectedFace.id
            ? { ...f, person: personId, person_name: `${personForm.first_name || ""} ${personForm.last_name || ""}`.trim() }
            : f
        )
      );
      setSelectedFace(null);
      closePersonModal();
      setModalPersonId(null);
      loadData();
    } catch (e) {
      console.error("Failed to save person:", e);
    } finally {
      setSavingPerson(false);
    }
  };

  const handleIgnoreFace = async (face) => {
    try {
      await ignoreFace(face.id);
      setFaces((prev) =>
        prev.map((f) => (f.id === face.id ? { ...f, ignored: true } : f))
      );
      loadData();
    } catch (e) {
      console.error("Failed to ignore face:", e);
    }
  };

  const handleUnignoreFace = async (face) => {
    try {
      await unignoreFace(face.id);
      setFaces((prev) =>
        prev.map((f) => (f.id === face.id ? { ...f, ignored: false } : f))
      );
      loadData();
    } catch (e) {
      console.error("Failed to unignore face:", e);
    }
  };

  const handleMarkDone = async () => {
    if (!selectedImage) return;
    const unlabeled = faces.filter((f) => !f.person && !f.ignored);
    if (unlabeled.length > 0) {
      alert(`Please label or ignore all ${unlabeled.length} remaining faces.`);
      return;
    }
    try {
      await markTrainingDone(selectedImage);
      const currentIndex = images.findIndex((i) => i.id === selectedImage);
      const nextImage = images[currentIndex + 1] || images[currentIndex - 1];
      setSelectedImage(nextImage?.id || null);
      loadData();
    } catch (e) {
      console.error("Failed to mark done:", e);
      alert("Failed: " + (e.response?.data?.error || e.message));
    }
  };

  const untrainedCount = (stats?.total || 0) - (stats?.by_status?.done || 0);

  const handleDeleteUntrained = async () => {
    setShowDeleteModal(false);
    setDeleting(true);
    try {
      const result = await deleteUntrainedImages();
      showToast(`Deleted ${result.deleted} training images`, "success");
      setSelectedImage(null);
      loadData();
    } catch (e) {
      console.error("Failed to delete untrained:", e);
      showToast(`Failed to delete: ${e.response?.data?.error || e.message}`, "error", 5000);
    } finally {
      setDeleting(false);
    }
  };

  // Load propagate candidates
  const loadPropagateCandidates = useCallback(async () => {
    setPropagateLoading(true);
    try {
      const data = await fetchPropagateCandidates(propagateThreshold, 20);
      setPropagateCandidates(data.candidates || []);
      setPropagateSummary(data.summary || null);
      setSelectedMatches({});
    } catch (e) {
      console.error("Failed to load propagate candidates:", e);
    } finally {
      setPropagateLoading(false);
    }
  }, [propagateThreshold]);

  // Load candidates when switching to propagate view
  useEffect(() => {
    if (viewMode === "propagate") {
      loadPropagateCandidates();
    }
  }, [viewMode, loadPropagateCandidates]);

  // Toggle match selection
  const toggleMatchSelection = (personId, faceId) => {
    setSelectedMatches((prev) => {
      const personSet = new Set(prev[personId] || []);
      if (personSet.has(faceId)) {
        personSet.delete(faceId);
      } else {
        personSet.add(faceId);
      }
      return { ...prev, [personId]: personSet };
    });
  };

  // Select all matches for a person
  const selectAllMatches = (personId, matches) => {
    setSelectedMatches((prev) => ({
      ...prev,
      [personId]: new Set(matches.map((m) => m.face_id)),
    }));
  };

  // Clear selection for a person
  const clearSelection = (personId) => {
    setSelectedMatches((prev) => ({
      ...prev,
      [personId]: new Set(),
    }));
  };

  // Assign selected matches to person
  const handleBulkAssign = async (personId) => {
    const faceIds = Array.from(selectedMatches[personId] || []);
    if (faceIds.length === 0) return;

    setAssigningPerson(personId);
    try {
      await bulkAssignPerson(faceIds, personId);
      // Reload candidates after assignment
      await loadPropagateCandidates();
      await loadData();
    } catch (e) {
      console.error("Failed to bulk assign:", e);
      alert("Failed to assign faces: " + e.message);
    } finally {
      setAssigningPerson(null);
    }
  };

  const progress = useMemo(() => {
    const labeled = faces.filter((f) => f.person).length;
    const ignored = faces.filter((f) => f.ignored).length;
    const total = faces.length;
    const remaining = total - labeled - ignored;
    return { labeled, ignored, total, remaining };
  }, [faces]);

  return (
    <div className="page">
      <div className="content">
        {/* View Mode Tabs */}
        <div className="row gap" style={{ marginBottom: 16 }} ref={tabsRef}>
          <button
            className={viewMode === "label" ? "" : "ghost"}
            onClick={() => setViewMode("label")}
          >
            Label Faces
          </button>
          <button
            className={viewMode === "propagate" ? "" : "ghost"}
            onClick={() => setViewMode("propagate")}
          >
            Propagate Labels
            {propagateSummary?.total_matches > 0 && (
              <span style={{ marginLeft: 6, opacity: 0.7 }}>({propagateSummary.total_matches})</span>
            )}
          </button>
        </div>

        {/* Label View */}
        {viewMode === "label" && (
        <div className={`tagging-main ${sidebarCollapsed ? "sidebar-collapsed" : ""}`}>
          {/* Main Panel - Image and Faces */}
          <div className="card tag-photo">
            <div className="row space-between" style={{ marginBottom: 12 }}>
              <div className="row gap">
                <h3 style={{ margin: 0 }}>
                  {currentImage ? currentImage.filename : "Training Mode"}
                </h3>
                {currentImage && (
                  <span style={{ color: "var(--muted)", fontSize: 13 }}>
                    {progress.labeled}/{progress.total} labeled
                    {progress.ignored > 0 && `, ${progress.ignored} ignored`}
                  </span>
                )}
              </div>
              <div className="row gap">
                <button className="ghost" onClick={loadData}>
                  Refresh
                </button>
                <button
                  className="ghost"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  {uploading ? "Uploading..." : "Upload"}
                </button>
                <button
                  className="ghost toggle-btn"
                  onClick={() => setSidebarCollapsed((v) => !v)}
                  title={sidebarCollapsed ? "Show sidebar" : "Hide sidebar"}
                >
                  {sidebarCollapsed ? "Show list" : "Hide list"}
                </button>
              </div>
            </div>

            {/* Hidden file input for upload */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png"
              multiple
              style={{ display: "none" }}
              onChange={handleFileSelect}
            />

            {/* Image Display with Face Overlay */}
            <div className="viz-block">
              <div className="viz-frame">
                {currentImage?.preview_url ? (
                  <div className="img-wrapper" style={{ position: "relative" }}>
                    <img
                      ref={imgRef}
                      src={currentImage.preview_url}
                      alt={currentImage.filename}
                      draggable={false}
                      onLoad={(e) => {
                        const img = e.target;
                        setImgDims({
                          naturalW: img.naturalWidth,
                          naturalH: img.naturalHeight,
                          displayW: img.clientWidth,
                          displayH: img.clientHeight,
                        });
                      }}
                    />
                    {/* Face bounding box overlay */}
                    {imgDims?.displayW > 0 && faces.length > 0 && currentImage?.width && currentImage?.height && (
                      <svg
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: imgDims.displayW,
                          height: imgDims.displayH,
                          pointerEvents: "none",
                        }}
                        viewBox={`0 0 ${currentImage.width} ${currentImage.height}`}
                      >
                        <FaceBboxOverlay
                          faces={faces}
                          selectedFace={selectedFace}
                          onFaceClick={handleFaceClick}
                        />
                      </svg>
                    )}
                  </div>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "var(--muted)",
                      padding: 40,
                      minHeight: 200,
                      border: dragActive ? "2px dashed var(--accent)" : "2px dashed transparent",
                      borderRadius: 8,
                      background: dragActive ? "rgba(104, 255, 154, 0.05)" : "transparent",
                      transition: "all 0.2s ease",
                    }}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    {(() => {
                      if (loading) return <span>Loading...</span>;
                      if (uploading) return <span>Uploading...</span>;
                      if (images.length === 0) return (
                        <>
                          <span style={{ marginBottom: 12 }}>Drop portrait images here to upload</span>
                          <button onClick={() => fileInputRef.current?.click()}>
                            Select Files
                          </button>
                        </>
                      );
                      return <span>Select an image</span>;
                    })()}
                  </div>
                )}
              </div>
            </div>

            {/* Faces Grid */}
            {currentImage && faces?.length > 0 && (
              <div className="card" style={{ marginTop: 12, marginBottom: 0 }}>
                <div className="row space-between" style={{ marginBottom: 10 }}>
                  <span style={{ fontWeight: 600 }}>Detected Faces ({faces.length})</span>
                  <span style={{ color: "var(--muted)", fontSize: 12 }}>
                    Click to assign • Right-click to ignore
                  </span>
                </div>
                <div className="face-grid" style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 12,
                }}>
                  {faces.map((face) => (
                    <FaceThumb
                      key={face.id}
                      face={face}
                      isSelected={selectedFace?.id === face.id}
                      onFaceClick={handleFaceClick}
                      onIgnore={handleIgnoreFace}
                      onUnignore={handleUnignoreFace}
                      previewUrl={currentImage?.preview_url}
                      imageWidth={currentImage?.width || imgDims.naturalW}
                      imageHeight={currentImage?.height || imgDims.naturalH}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            {currentImage && (
              <div className="row end gap" style={{ marginTop: 12 }}>
                <button
                  onClick={handleMarkDone}
                  disabled={progress.remaining > 0}
                >
                  Mark Training Done
                  {progress.remaining > 0 && ` (${progress.remaining} remaining)`}
                </button>
              </div>
            )}
          </div>

          {/* Sidebar - Image List & Stats */}
          <div className="tag-neighbors">
            {/* Stats Card */}
            <div className="card" style={{ flexShrink: 0 }} ref={statsCardRef}>
              <h4 style={{ margin: "0 0 8px", color: "var(--accent)" }}>Training Stats</h4>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 12px", fontSize: 13 }}>
                <span style={{ color: "var(--muted)" }}>Ready</span>
                <span style={{ textAlign: "right" }}>{stats?.by_status?.ready || 0}</span>
                <span style={{ color: "var(--muted)" }}>Total Faces</span>
                <span style={{ textAlign: "right" }}>{stats?.faces?.total || 0}</span>
                <span style={{ color: "var(--muted)" }}>Labeled</span>
                <span style={{ textAlign: "right", color: "var(--accent)" }}>{stats?.faces?.labeled || 0}</span>
                <span style={{ color: "var(--muted)" }}>Unlabeled</span>
                <span style={{ textAlign: "right" }}>{stats?.faces?.unlabeled || 0}</span>
              </div>
              {/* Delete untrained button */}
              {untrainedCount > 0 && (
                <>
                  <div style={{ borderTop: "1px solid var(--color-border, #102420)", margin: "12px 0" }} />
                  <Button
                    variant="ghost"
                    size="sm"
                    fullWidth
                    loading={deleting}
                    onClick={() => setShowDeleteModal(true)}
                    style={{ color: "var(--color-danger, #ff6b6b)" }}
                  >
                    Delete All Untrained ({untrainedCount})
                  </Button>
                </>
              )}
            </div>

            {/* Image List */}
            <div className="card" style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
              <h4 style={{ margin: "0 0 8px" }}>Images ({images.length})</h4>
              <div style={{ flex: 1, overflowY: "auto", margin: "0 -12px", padding: "0 12px" }} ref={imageListRef}>
                {images.map((img) => (
                  <div
                    key={img.id}
                    data-image-item
                    onClick={() => setSelectedImage(img.id)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "8px 10px",
                      marginBottom: 4,
                      borderRadius: 8,
                      cursor: "pointer",
                      background: selectedImage === img.id ? "rgba(104, 255, 154, 0.15)" : "transparent",
                      border: selectedImage === img.id ? "1px solid var(--accent)" : "1px solid transparent",
                      transition: "all 0.15s ease",
                    }}
                  >
                    {img.thumb_url && (
                      <img
                        src={img.thumb_url}
                        alt=""
                        style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 4 }}
                      />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {img.filename}
                      </div>
                      <div style={{ fontSize: 11, color: "var(--muted)" }}>
                        {img.face_count} faces • {img.unlabeled_count || 0} unlabeled
                      </div>
                    </div>
                  </div>
                ))}
                {images.length === 0 && !loading && (
                  <div style={{ color: "var(--muted)", fontSize: 13, padding: 12, textAlign: "center" }}>
                    No training images ready
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Propagate View */}
        {viewMode === "propagate" && (
          <div className="propagate-view">
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="row space-between">
                <div>
                  <h3 style={{ margin: 0 }}>Propagate Labels</h3>
                  <p style={{ margin: "4px 0 0", color: "var(--muted)", fontSize: 13 }}>
                    Review similar faces and bulk-assign labels
                  </p>
                </div>
                <div className="row gap" style={{ alignItems: "center" }}>
                  <span style={{ fontSize: 13, color: "var(--muted)" }}>Threshold:</span>
                  <select
                    value={propagateThreshold}
                    onChange={(e) => setPropagateThreshold(parseFloat(e.target.value))}
                  >
                    <option value="0.3">Strict (70%+)</option>
                    <option value="0.4">High (60%+)</option>
                    <option value="0.5">Medium (50%+)</option>
                    <option value="0.6">Low (40%+)</option>
                  </select>
                  <button className="ghost" onClick={loadPropagateCandidates}>
                    Refresh
                  </button>
                </div>
              </div>
              {propagateSummary && (
                <div className="row gap" style={{ marginTop: 12, fontSize: 13 }}>
                  <span>Labeled: <strong>{propagateSummary.labeled_faces}</strong></span>
                  <span>•</span>
                  <span>Unlabeled: <strong>{propagateSummary.unlabeled_faces}</strong></span>
                  <span>•</span>
                  <span>Matches found: <strong style={{ color: "var(--accent)" }}>{propagateSummary.total_matches}</strong></span>
                </div>
              )}
            </div>

            {propagateLoading ? (
              <div className="card" style={{ textAlign: "center", padding: 40, color: "var(--muted)" }}>
                Loading candidates...
              </div>
            ) : propagateCandidates.length === 0 ? (
              <div className="card" style={{ textAlign: "center", padding: 40, color: "var(--muted)" }}>
                No propagation candidates found. Label more faces first, or lower the threshold.
              </div>
            ) : (
              <div className="propagate-candidates">
                {propagateCandidates.filter((c) => c.matches.length > 0).map((candidate, idx) => (
                  <div key={candidate.person_id} className="card propagate-person-card" style={{ marginBottom: 16 }} ref={el => propagateCardsRef.current[idx] = el}>
                    <div className="row space-between" style={{ marginBottom: 12 }}>
                      <div>
                        <h4 style={{ margin: 0, color: "var(--accent)" }}>{candidate.person_name}</h4>
                        <span style={{ fontSize: 12, color: "var(--muted)" }}>
                          {candidate.labeled_faces.length} labeled face(s) • {candidate.matches.length} match(es)
                        </span>
                      </div>
                      <div className="row gap">
                        <button
                          className="ghost"
                          onClick={() => selectAllMatches(candidate.person_id, candidate.matches)}
                          disabled={candidate.matches.length === 0}
                        >
                          Select All
                        </button>
                        <button
                          className="ghost"
                          onClick={() => clearSelection(candidate.person_id)}
                          disabled={!selectedMatches[candidate.person_id]?.size}
                        >
                          Clear
                        </button>
                        <button
                          onClick={() => handleBulkAssign(candidate.person_id)}
                          disabled={!selectedMatches[candidate.person_id]?.size || assigningPerson === candidate.person_id}
                        >
                          {assigningPerson === candidate.person_id ? "Assigning..." : `Assign (${selectedMatches[candidate.person_id]?.size || 0})`}
                        </button>
                      </div>
                    </div>

                    {candidate.matches.length > 0 ? (
                      <div className="propagate-matches" style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
                        gap: 12,
                      }}>
                        {candidate.matches.map((match) => {
                          const isSelected = selectedMatches[candidate.person_id]?.has(match.face_id);
                          return (
                            <div
                              key={match.face_id}
                              className={`propagate-match-card ${isSelected ? "selected" : ""}`}
                              onClick={() => toggleMatchSelection(candidate.person_id, match.face_id)}
                              style={{
                                position: "relative",
                                cursor: "pointer",
                                border: isSelected ? "2px solid var(--accent)" : "2px solid transparent",
                                borderRadius: 8,
                                padding: 8,
                                background: isSelected ? "rgba(104, 255, 154, 0.1)" : "rgba(10, 20, 30, 0.5)",
                                transition: "all 0.15s ease",
                              }}
                            >
                              {match.preview_url && (
                                <div style={{ position: "relative", marginBottom: 8 }}>
                                  <img
                                    src={match.preview_url}
                                    alt=""
                                    style={{
                                      width: "100%",
                                      height: 120,
                                      objectFit: "cover",
                                      borderRadius: 4,
                                    }}
                                  />
                                  {/* Bbox overlay */}
                                  {match.bbox && (
                                    <div
                                      style={{
                                        position: "absolute",
                                        top: 4,
                                        right: 4,
                                        background: "rgba(0,0,0,0.7)",
                                        padding: "2px 6px",
                                        borderRadius: 4,
                                        fontSize: 11,
                                      }}
                                    >
                                      Face detected
                                    </div>
                                  )}
                                </div>
                              )}
                              <div style={{ fontSize: 12 }}>
                                <div style={{
                                  color: (() => {
                                    if (match.confidence >= 70) return "var(--accent)";
                                    if (match.confidence >= 50) return "#ffa500";
                                    return "var(--muted)";
                                  })(),
                                  fontWeight: 600,
                                }}>
                                  {match.confidence}% match
                                </div>
                                <div style={{ color: "var(--muted)", fontSize: 11, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {match.filename || match.image_id}
                                </div>
                              </div>
                              {isSelected && (
                                <div style={{
                                  position: "absolute",
                                  top: 4,
                                  left: 4,
                                  background: "var(--accent)",
                                  color: "#000",
                                  width: 20,
                                  height: 20,
                                  borderRadius: "50%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontWeight: "bold",
                                  fontSize: 14,
                                }}>
                                  ✓
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div style={{ color: "var(--muted)", fontSize: 13 }}>
                        No matches found for this person
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Person Modal - shared component from Edit page */}
      <PersonModal
        isOpen={showPersonModal}
        position={personModalPos}
        rainEnabled={false}
        personForm={personForm}
        personSuggestions={personSuggestions}
        highConfNeighbors={[]}
        selectedFace={selectedFace}
        savingPerson={savingPerson}
        teamSearchQuery={teamSearchQuery}
        teamSuggestions={teamSuggestions}
        selectedTeam={selectedTeam}
        onTeamSearchChange={setTeamSearchQuery}
        onSelectTeam={selectTeam}
        onClearTeam={clearTeam}
        onFormChange={setPersonForm}
        onSelectSuggestion={(p) => {
          const firstMembership = p.team_memberships?.[0];
          setPersonForm({
            prefix: p.prefix || "",
            first_name: p.first_name || "",
            last_name: p.last_name || "",
            suffix: p.suffix || "",
            jersey_number: p.jersey_number != null ? String(p.jersey_number) : "",
            team_id: firstMembership?.team_id || "",
          });
          if (firstMembership?.team_id) {
            selectTeam({ id: firstMembership.team_id, name: firstMembership.team_name });
          } else {
            clearTeam();
          }
          setModalPersonId(p.id);
        }}
        onSave={handleSavePerson}
        onClose={() => { closePersonModal(); setModalPersonId(null); }}
        onDragStart={handleModalDragStart}
      />

      {/* Delete Confirmation Modal */}
      <Modal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} size="sm">
        <Modal.Header onClose={() => setShowDeleteModal(false)}>
          Delete Untrained Images
        </Modal.Header>
        <Modal.Body>
          <p style={{ margin: 0, color: "var(--color-text, #dfe8e1)" }}>
            Delete <strong>{untrainedCount}</strong> untrained training images?
          </p>
          <div style={{
            marginTop: 12,
            padding: 12,
            background: "rgba(255, 107, 107, 0.1)",
            borderRadius: 8,
            border: "1px solid rgba(255, 107, 107, 0.2)"
          }}>
            <p style={{ margin: 0, fontSize: 13, color: "var(--color-danger, #ff6b6b)" }}>
              This action cannot be undone. All images not marked as done will be permanently deleted along with their detected faces.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="ghost" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" loading={deleting} onClick={handleDeleteUntrained}>
            Delete {untrainedCount} Images
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Toast notifications */}
      {toast && <Toast {...toast} onClose={hideToast} />}
    </div>
  );
}
